import React, { forwardRef } from 'react'
import { cn } from '../utils/cn'
import { Skeleton } from './Skeleton'

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  value: string | number
  icon?: React.ComponentType<any>
  iconBgColor?: string
  iconColor?: string
  loading?: boolean
  description?: string
  trend?: {
    value: number
    label?: string
    direction: 'up' | 'down' | 'neutral'
  }
}

const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({
    className,
    title,
    value,
    icon: IconComponent,
    iconBgColor = '#3B82F6',
    iconColor = '#FFFFFF',
    loading = false,
    description,
    trend,
    ...props
  }, ref) => {
    if (loading) {
      return (
        <div
          ref={ref}
          className={cn(
            'bg-white border border-gray-200 rounded-xl p-4 sm:p-6',
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-3">
            <Skeleton variant="circular" width={40} height={40} />
            <div className="flex-1 space-y-2">
              <Skeleton width="60%" height={16} />
              <Skeleton width="40%" height={24} />
            </div>
          </div>
        </div>
      )
    }

    const isHexColor = (color: string) => color.startsWith('#')

    const getTrendColor = (direction: string) => {
      switch (direction) {
        case 'up':
          return 'text-green-600'
        case 'down':
          return 'text-red-600'
        default:
          return 'text-gray-600'
      }
    }

    const getTrendIcon = (direction: string) => {
      switch (direction) {
        case 'up':
          return '↗'
        case 'down':
          return '↘'
        default:
          return '→'
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow',
          className
        )}
        {...props}
      >
        <div className="flex items-center gap-3">
          {IconComponent && (
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: iconBgColor }}
            >
              <IconComponent
                className="w-5 h-5"
                style={{ color: iconColor }}
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-600 truncate">
              {title}
            </p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-gray-900 truncate">
                {value}
              </p>
              {trend && (
                <span className={cn('text-sm flex items-center gap-1', getTrendColor(trend.direction))}>
                  <span>{getTrendIcon(trend.direction)}</span>
                  <span>{trend.value}%</span>
                  {trend.label && <span className="text-gray-500">({trend.label})</span>}
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }
)

StatCard.displayName = 'StatCard'

export { StatCard } 